import React, {memo, useCallback, useEffect} from 'react';
import {useRPC} from '../../../../hooks/rpc';
import {CONTROL_TYPE_ID_SIREN, ControlModel, InputModel, SensorTypeModel} from '../../../../types/control';
import './index.scss';
import {useDispatch} from 'react-redux';
import {entityUpdatedAction, inputSendErrorAction} from '../../../../store/control/actions';
import {Column} from '../../../../components/Column';
import {onClickHandler} from '../../../../utils/onclick';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useSetSelector} from '../../../../hooks/set-selector';
import {useFilterSelector} from '../../../../hooks/filter-selector';

const getImage = (sensor: InputModel, set: { [key: string]: SensorTypeModel }) => {
    const type = set[sensor.sensor_settings.sensor_type_id];
    if (!type) {
        return null;
    }

    if (sensor.is_active) {
        return type.image_set.active;
    }

    return type.image_set.passive;
};

type SensorEditProps = {
    sensorOrSiren: InputModel | ControlModel;
    isInput: boolean;
    icon: string;
}

export const SensorEditComponent = memo(function SensorEditComponent({sensorOrSiren, icon, isInput}: SensorEditProps) {
    const [rpc] = useRPC();
    const dispatch = useDispatch();

    useEffect(() => {
        if (sensorOrSiren.hasError) {
            const timeout = setTimeout(() => dispatch(inputSendErrorAction(sensorOrSiren.id, false)), 3000);
            return () => clearTimeout(timeout);
        }
    }, [dispatch, sensorOrSiren]);

    const changeStatus = useCallback((sensor: InputModel | ControlModel, is_active: boolean) => {
        if (is_active === sensor.is_active) {
            return;
        }

        dispatch(entityUpdatedAction({...sensor, isUpdating: true}, isInput ? 'input' : 'control'));

        rpc.updateSensorStatus(sensor.id, is_active, !isInput);
    }, [dispatch, rpc, isInput]);

    return (
        <Column min={6} max={12}>
            <div className="device">
                <div className="device-name">
                    {sensorOrSiren.name}
                </div>
                <img
                    src={icon}
                    alt=''/>
                <div className="device-body">
                    <div
                        className={'device-button ' + (sensorOrSiren.is_active ? 'selected' : '')}
                        {...onClickHandler(() => changeStatus(sensorOrSiren, true))}>
                        I
                    </div>
                    <div
                        className={'device-button ' + (sensorOrSiren.is_active ? '' : 'selected')}
                        {...onClickHandler(() => changeStatus(sensorOrSiren, false))}>
                        0
                    </div>
                </div>
                {
                    renderIf(sensorOrSiren.isUpdating)(() =>
                        <div className="open-close-button-loading"/>,
                    )
                }
                {
                    renderIf(sensorOrSiren.hasError)(() =>
                        <div className="open-close-button-loading darker">
                            <span>Cihaza ulaşılamıyor.</span>
                        </div>,
                    )
                }
            </div>
        </Column>
    );
});

export const SensorSettingsScreen = memo(function SensorSettingsScreen() {
    const inputTypeSet = useSetSelector(state => state.control.inputTypeList);
    const sensorList = useFilterSelector(state => state.control.inputList, i => inputTypeSet[i.type_id]?.primitive_name === 'sensor', [inputTypeSet]);
    const sensorTypeSet = useSetSelector(state => state.control.sensorTypeList);
    const sirenList = useFilterSelector(state => state.control.controlList, c => c.type_id === CONTROL_TYPE_ID_SIREN, []);
    const controlTypeSet = useSetSelector(state => state.control.controlTypeList);

    return (
        <PasswordContainer requiredRole='admin' isFullscreen={true}>
            <SettingsPageContainer buttons={[]} backUrl={'/settings'}>
                <div className="page-wrapper">
                    <div className="sensor-settings">
                        {
                            sensorList.map(sensor =>
                                <SensorEditComponent
                                    key={`sensor_edit_${sensor.id}`}
                                    icon={getImage(sensor, sensorTypeSet)}
                                    sensorOrSiren={sensor}
                                    isInput={true}/>,
                            )
                        }
                        {
                            sirenList.map(siren =>
                                <SensorEditComponent
                                    key={`siren_edit_${siren.id}`}
                                    icon={controlTypeSet[siren.type_id]?.image_set?.default || null}
                                    sensorOrSiren={siren}
                                    isInput={false}/>,
                            )
                        }
                    </div>
                </div>
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
